/**
 * Firebase app singleton (modular v9+ API).
 * Initialized once per browser session; safe to import from services and context.
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  getFirestore,
  enableIndexedDbPersistence,
  connectFirestoreEmulator,
} from 'firebase/firestore';

import appSettings from '../config/settings';
import { firebaseConfig, isFirebaseConfigured } from './config';

let firebaseApp = null;
let auth = null;
let db = null;
let persistenceEnabled = false;
let authEmulatorConnected = false;
let firestoreEmulatorConnected = false;

const useEmulator = appSettings.dev && process.env.REACT_APP_FIREBASE_USE_EMULATOR === 'true';

/**
 * Returns the initialized Firebase app or null when env config is missing.
 */
export const getFirebaseApp = () => {
  if (!isFirebaseConfigured()) {
    return null;
  }
  if (!firebaseApp) {
    firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
  }
  return firebaseApp;
};

export const getFirebaseAuth = () => {
  const app = getFirebaseApp();
  if (!app) {
    return null;
  }
  if (!auth) {
    auth = getAuth(app);
    if (useEmulator && !authEmulatorConnected) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      authEmulatorConnected = true;
      if (appSettings.dev) {
        console.info('[Firebase] Auth emulator: localhost:9099');
      }
    }
  }
  return auth;
};

export const getFirestoreDb = () => {
  const app = getFirebaseApp();
  if (!app) {
    return null;
  }
  if (!db) {
    db = getFirestore(app);
    if (useEmulator && !firestoreEmulatorConnected) {
      // Only use when `firebase emulators:start` is running; otherwise writes fail with
      // WebChannelConnection "transport errored" (undefined message).
      connectFirestoreEmulator(db, 'localhost', 8080);
      firestoreEmulatorConnected = true;
      if (appSettings.dev) {
        console.info('[Firebase] Firestore emulator: localhost:8080');
      }
    }
  }
  return db;
};

/**
 * Enables offline persistence once (best-effort; fails in multi-tab without harm).
 */
export const enableFirestoreOffline = async () => {
  const firestore = getFirestoreDb();
  if (!firestore || persistenceEnabled) {
    return;
  }
  try {
    await enableIndexedDbPersistence(firestore);
    persistenceEnabled = true;
  } catch (err) {
    // failed-precondition: multiple tabs open
    if (err?.code !== 'failed-precondition') {
      console.warn('[Firebase] Offline persistence unavailable:', err?.message);
    }
  }
};

/**
 * Email/password sign-in for the blog CMS (separate from Sharetribe accounts).
 * Replaces any existing Firebase session (including wishlist custom tokens).
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signInBlogAdmin = async (email, password) => {
  const authInstance = getFirebaseAuth();
  if (!authInstance) {
    throw new Error('Firebase Auth is not configured');
  }
  const credential = await signInWithEmailAndPassword(authInstance, email, password);
  // Force-refresh so custom claims (admin: true) are present for Firestore rules.
  await credential.user.getIdToken(true);
  return credential;
};

export { signInWithCustomToken, signInWithEmailAndPassword, signOut, onAuthStateChanged };
